import React, { useState, useMemo, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Copy as CopyIcon, CheckCircle as CopyCheck, Eye, Pencil, Maximize2, X } from "lucide-react";
import { buildStyledHtml } from "../../utils/buildStyledHtml";

const Documentation = ({ children }: { children: React.ReactNode }) => {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"preview" | "raw">("preview");
  const [isOpen, setIsOpen] = useState(false);

  const rawContent = String(children).replace(/\n$/, "");
  const previewHtml = useMemo(() => buildStyledHtml(rawContent), [rawContent]);

  const handleCopyContent = (content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyButton = (
    <button
      className="flex gap-1 items-center"
      onClick={() => handleCopyContent(rawContent)}
    >
      {copied ? (
        <>
          <CopyCheck size={12} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <CopyIcon size={12} />
          <span>Copy</span>
        </>
      )}
    </button>
  );

  return (
    <>
      {/* ── Inline card ─────────────────────────────────────────── */}
      <div className="mx-auto sm:max-w-3xl md:max-w-2xl lg:max-w-3xl text-sm rounded-lg my-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between bg-token-main-surface-secondary px-4 py-2 text-xs font-sans border-b border-gray-200">
            <span className="text-gray-600">Notepad</span>
            <div className="flex items-center gap-2">
              {copyButton}
              <button
                onClick={() => setIsOpen(true)}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Full view"
              >
                <Maximize2 size={13} />
              </button>
            </div>
          </div>

          {/* Inline preview */}
          <div className="text-gray-800">
            <iframe
              srcDoc={previewHtml}
              title="Notepad preview"
              className="w-full border-0 bg-white"
              sandbox="allow-same-origin"
              style={{ display: "block", height: "400px" }}
            />
          </div>
        </div>
      </div>

      {/* ── Full-view modal (same pattern as SettingsDialog) ───── */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-2xl text-left align-middle shadow-xl transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[80vh] lg:h-[650px] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-semibold text-gray-800">Notepad</h2>
                      {/* Mode toggle */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                        <button
                          onClick={() => setMode("preview")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "preview"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Eye size={13} />
                          Preview
                        </button>
                        <button
                          onClick={() => setMode("raw")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "raw"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Pencil size={13} />
                          Raw
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {copyButton}
                      <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-hidden">
                    {mode === "preview" ? (
                      <iframe
                        srcDoc={previewHtml}
                        title="Notepad preview"
                        className="w-full h-full border-0 bg-white"
                        sandbox="allow-same-origin"
                        style={{ display: "block" }}
                      />
                    ) : (
                      <div className="p-4 whitespace-pre-wrap font-mono text-[13px] overflow-y-auto h-full text-gray-800">
                        {rawContent}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Documentation;
